import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.addEventListener(ThemeEvents.variantSelected, this.updatePriceImmediately);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.removeEventListener(ThemeEvents.variantSelected, this.updatePriceImmediately);
  }

  /**
   * Updates the visible price synchronously from the local variant index.
   * @param {import('./events').VariantSelectedEvent} event
   */
  updatePriceImmediately = (event) => {
    if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) return;

    const variant = event.detail.variant;
    if (!variant?.id || variant.price == null) return;

    const currency = this.dataset.currency || 'USD';
    const moneyFormat =
      this.dataset.useCurrency === 'true'
        ? this.dataset.moneyWithCurrencyFormat || this.dataset.moneyFormat || '{{amount}}'
        : this.dataset.moneyFormat || '{{amount}}';
    const price = formatMoney(Number(variant.price), moneyFormat, currency);
    const compareAtPrice = Number(variant.compare_at_price || 0);
    const isOnSale = compareAtPrice > Number(variant.price);

    this.querySelectorAll('[data-aknes-price]').forEach((element) => {
      element.innerHTML = price;
    });

    this.querySelectorAll('[data-aknes-compare-price]').forEach((element) => {
      element.innerHTML = isOnSale ? formatMoney(compareAtPrice, moneyFormat, currency) : '';
      const group = element.closest('.price-item__group');
      if (group instanceof HTMLElement) group.hidden = !isOnSale;
    });

    this.querySelector('.price__regular')?.classList.toggle('price__hidden', isOnSale);
    this.querySelector('.price__sale')?.classList.toggle('price__hidden', !isOnSale);

    const installmentsInput = /** @type {HTMLInputElement|null} */ (
      this.querySelector(`#product-form-installment-${this.dataset.blockId} input[name="id"]`)
    );
    if (installmentsInput && installmentsInput.value !== String(variant.id)) {
      installmentsInput.value = String(variant.id);
      installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const { priceContainer, volumePricingNote } = this.refs;
    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(
      `product-price[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newProductPrice) return;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice);
    }

    // Update volume pricing note
    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      // Use newPrice since priceContainer was just replaced and now points to the detached element
      newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      volumePricingNote.replaceWith(newNote);
    }

    // Update installments (SPI banner) variant ID to trigger payment terms re-render
    const input_selector = `#product-form-installment-${this.dataset.blockId} input[name="id"]`;
    const installmentsInput = /** @type {HTMLInputElement|null} */ (this.querySelector(input_selector));
    if (installmentsInput) {
      installmentsInput.value = event.detail.resource?.id ?? '';
      installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
